import classNames from 'classnames';
import { ConditionalWrapper } from 'components/ConditionalWrapper/ConditionalWrapper';
import { ReactComponent as ChallengeIcon } from 'components/icons/challengeIcon.svg';
import { ReactComponent as WarningIcon } from 'components/icons/warning-icon.svg';
import { ServiceTypes } from 'constants/ServiceTypes';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { observer } from 'mobx-react-lite';
import { Play } from 'models/tagging/Play';
import React, { useRef, useState } from 'react';
import { Collapse } from 'react-collapse';
import { useInjection } from 'services/ioc/ioc.react';
import { TaggingStore } from 'stores/Tagging/TaggingStore';
import {
  FirstRow,
  NotSaved,
  PlaySequenceNumber,
  PlayWrapper,
  Player,
  Score,
  StyledExpandIcon,
  TooltipText,
  Type,
  WarmupOption,
  Warning,
} from './PlayComponentStyles';

export interface IProps {
  play: Play;
  onClick: (play: Play) => void;
  isSelected?: boolean;
}
export const PlayComponent: React.FC<IProps> = observer((props: IProps) => {
  const { onClick, isSelected, play } = props;
  const { batter, balls, tag, strikes, playSequenceNumber } = play;
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const taggingStore = useInjection<TaggingStore>(ServiceTypes.TaggingStore);
  const wrapperRef = useRef<any>();
  const { playStore } = taggingStore;

  useOnClickOutside(
    wrapperRef,
    () => {
      setIsOpened(false);
    },
    'click'
  );

  const playComponentClasses = classNames({
    selected: isSelected,
    even: playSequenceNumber && playSequenceNumber % 2 === 0,
    odd: playSequenceNumber && playSequenceNumber % 2 !== 0,
    animate: play.shouldAnimate,
  });

  const handleOnClickExpandIcon = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    setIsOpened(!isOpened);
  };

  return play.isWarmup === false ? (
    <ConditionalWrapper
      condition={!play.isTagStored && !isSelected}
      wrapper={(children: React.ReactElement) => <NotSaved>{children}</NotSaved>}
    >
      <PlayWrapper
        ref={wrapperRef}
        className={`${playComponentClasses}`}
        onClick={() => onClick(play)}
        data-testid={`play-item-${!play.isWarmup}`}
      >
        <FirstRow>
          <Player>{batter.nameRef}</Player>
          {!play.isTagStored && !isSelected && (
            <Warning>
              <WarningIcon />
              <TooltipText>Play not saved</TooltipText>
            </Warning>
          )}
          <PlaySequenceNumber>
            {play.challengeTag && <ChallengeIcon />}
            <div>{play.playSequenceNumber}</div>
          </PlaySequenceNumber>
        </FirstRow>
        <Score>
          {balls.total} - {strikes.total}
        </Score>

        <Type>{tag.pitchCallDisplay || '-'}</Type>

        <StyledExpandIcon className={`${isOpened ? ' opened' : ''}`} onClick={handleOnClickExpandIcon} />
        <Collapse isOpened={isOpened}>
          <WarmupOption onClick={() => playStore.openChallengeModalForPlay(play)} data-testid={'challenge-play'}>
            {play.challengeTag ? 'Edit challenge' : 'Call challenged'}
          </WarmupOption>
          <WarmupOption onClick={() => playStore.convertPlayToWarmup(play, play.inningNumber)} data-testid={'change-to-warmup'}>
            Change to warmup
          </WarmupOption>
        </Collapse>
      </PlayWrapper>
    </ConditionalWrapper>
  ) : null;
});
